import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPrint, FaSave, FaEye, FaCheck } from 'react-icons/fa';
import { MdViewModule } from 'react-icons/md';
import { fetchTemplatePreferences, saveTemplatePreferences } from '../../services/ClientService';
import { generateSampleReceipt } from '../../utils/thermalTemplates';

const BILL_SIZES = [
    { id: '40mm', name: '40mm', width: 40, description: 'Compact receipt' },
    { id: '50mm', name: '50mm', width: 50, description: 'Small receipt' },
    { id: '58mm', name: '58mm', width: 58, description: 'Standard compact' },
    { id: '80mm', name: '80mm', width: 80, description: 'Full-size receipt (most common)' }
];

const TEMPLATE_DESIGNS = [
    {
        id: 'classic',
        name: 'Classic Professional',
        description: 'Traditional receipt with clean lines and professional formatting',
        thumbnail: '📄',
        features: ['Bold headers', 'Table format', 'GST breakdown']
    },
    {
        id: 'modern',
        name: 'Modern Minimalist',
        description: 'Contemporary design with emphasis on clarity and spacing',
        thumbnail: '✨',
        features: ['Large spacing', 'Subtle borders', 'Icons']
    },
    {
        id: 'compact',
        name: 'Compact Saver',
        description: 'Maximizes content while minimizing paper usage',
        thumbnail: '📦',
        features: ['Dense layout', 'Small fonts', 'Efficient spacing']
    },
    {
        id: 'elegant',
        name: 'Elegant Boutique',
        description: 'Sophisticated design with decorative elements',
        thumbnail: '💎',
        features: ['Decorative borders', 'Elegant fonts', 'Logo emphasis']
    },
    {
        id: 'bold',
        name: 'Bold Impact',
        description: 'High-contrast design with strong visual hierarchy',
        thumbnail: '🎯',
        features: ['Large headers', 'Bold totals', 'High contrast']
    },
    {
        id: 'retail',
        name: 'Retail Standard',
        description: 'Industry-standard retail receipt format',
        thumbnail: '🏪',
        features: ['Standard layout', 'Barcode ready', 'Product focus']
    },
    {
        id: 'premium',
        name: 'Premium Luxury',
        description: 'Upscale design for high-end retail',
        thumbnail: '👑',
        features: ['Logo prominent', 'Premium spacing', 'Brand focus']
    },
    {
        id: 'eco',
        name: 'Eco Friendly',
        description: 'Minimal ink usage with optimized layout',
        thumbnail: '🌿',
        features: ['Light design', 'Minimal graphics', 'Paper saving']
    },
    {
        id: 'tech',
        name: 'Tech Modern',
        description: 'Contemporary tech-inspired design',
        thumbnail: '⚡',
        features: ['QR code ready', 'Modern icons', 'Digital style']
    },
    {
        id: 'traditional',
        name: 'Traditional Market',
        description: 'Classic market-style receipt',
        thumbnail: '🏛️',
        features: ['Simple layout', 'Easy to read', 'Traditional format']
    }
];

const TemplateManager = () => {
    const [selectedSize, setSelectedSize] = useState('80mm');
    const [selectedTemplate, setSelectedTemplate] = useState('classic');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        loadPreferences();
    }, []);

    // Robust body scroll lock to avoid layout shifts when modal opens
    const scrollYRef = useRef(0);
    useEffect(() => {
        if (showPreviewModal) {
            // save current scroll
            scrollYRef.current = window.scrollY || window.pageYOffset || 0;
            // compute scrollbar width to avoid layout shift when removing scrollbar
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollYRef.current}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
        } else {
            // restore
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.paddingRight = '';
            const prev = scrollYRef.current || 0;
            window.scrollTo(0, prev);
        }
        return () => {
            // cleanup in case component unmounts
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.paddingRight = '';
        };
    }, [showPreviewModal]);

    // (scroll lock implemented above with scrollYRef) - duplicate removed

    const loadPreferences = async () => {
        try {
            const response = await fetchTemplatePreferences();
            if (response.success && response.data) {
                setSelectedSize(response.data.billSize || '80mm');
                setSelectedTemplate(response.data.templateId || 'classic');
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePreferences = async () => {
        setIsSaving(true);
        try {
            const response = await saveTemplatePreferences({
                billSize: selectedSize,
                templateId: selectedTemplate
            });
            
            if (response.success) {
                showSnackbar('Template preferences saved successfully!', 'success');
            } else {
                showSnackbar('Failed to save preferences', 'error');
            }
        } catch (error) {
            console.error('Error saving preferences:', error);
            showSnackbar('Error saving preferences', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const showSnackbar = (message, type = 'success') => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: '', type: 'success' }), 3000);
    };

    const handlePreview = (templateId) => {
        setPreviewTemplate(templateId);
        setShowPreviewModal(true);
    };

    const handleTestPrint = () => {
        const printWindow = window.open('', '_blank');
        const htmlContent = generateSampleReceipt(selectedTemplate, selectedSize);
        
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 h-full p-6">
            {/* Snackbar */}
            <AnimatePresence>
                {snackbar.show && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className={`fixed top-4 right-4 ${
                            snackbar.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                        } text-white px-6 py-3 rounded-lg shadow-lg z-50`}
                    >
                        {snackbar.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <MdViewModule className="text-indigo-600" />
                        Template Manager
                    </h1>
                    <p className="text-gray-600">
                        Customize your thermal printer receipt templates and bill size preferences
                    </p>
                </div>

                {/* Bill Size Selection */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                        Select Bill Size
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {BILL_SIZES.map((size) => (
                            <motion.button
                                key={size.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setSelectedSize(size.id)}
                                className={`p-4 rounded-lg border-2 transition-all ${
                                    selectedSize === size.id
                                        ? 'border-indigo-600 bg-indigo-50 shadow-md'
                                        : 'border-gray-200 hover:border-indigo-300'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xl font-bold text-gray-800">
                                        {size.name}
                                    </span>
                                    {selectedSize === size.id && (
                                        <FaCheck className="text-indigo-600" />
                                    )}
                                </div>
                                <p className="text-sm text-gray-600">{size.description}</p>
                                <p className="text-xs text-gray-500 mt-1">Width: {size.width}mm</p>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* Template Selection */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                        Select Receipt Template
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {TEMPLATE_DESIGNS.map((template) => (
                            <motion.div
                                key={template.id}
                                whileHover={{ scale: 1.02 }}
                                className={`p-5 rounded-lg border-2 transition-all cursor-pointer ${
                                    selectedTemplate === template.id
                                        ? 'border-indigo-600 bg-indigo-50 shadow-md'
                                        : 'border-gray-200 hover:border-indigo-300'
                                }`}
                                onClick={() => setSelectedTemplate(template.id)}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl">{template.thumbnail}</span>
                                        <div>
                                            <h3 className="font-bold text-gray-800">
                                                {template.name}
                                            </h3>
                                            {selectedTemplate === template.id && (
                                                <span className="text-xs text-indigo-600 font-semibold">
                                                    Selected
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePreview(template.id);
                                        }}
                                        className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full transition"
                                        title="Preview"
                                    >
                                        <FaEye />
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">
                                    {template.description}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {template.features.map((feature, idx) => (
                                        <span
                                            key={idx}
                                            className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                                        >
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <button
                            onClick={handleSavePreferences}
                            disabled={isSaving}
                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
                        >
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave />
                                    Save Preferences
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleTestPrint}
                            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 rounded-lg font-bold text-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-lg flex items-center justify-center gap-2"
                        >
                            <FaPrint />
                            Test Print
                        </button>
                    </div>
                    <p className="text-sm text-gray-500 text-center mt-4">
                        Test print will show a sample receipt with your selected template and size
                    </p>
                </div>
            </div>

            {/* Preview Modal (iframe-based to isolate styles) */}
            <AnimatePresence>
                {showPreviewModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={() => setShowPreviewModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full h-[90vh] overflow-auto"
                            onClick={(e) => e.stopPropagation()}
                            style={{ height: '90vh' }}
                        >
                            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                                <h3 className="text-xl font-bold text-gray-800">
                                    Template Preview - {TEMPLATE_DESIGNS.find(t => t.id === previewTemplate)?.name}
                                </h3>
                                <button
                                    onClick={() => setShowPreviewModal(false)}
                                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="p-4 flex justify-center">
                                <div className="bg-gray-100 rounded-lg flex justify-center items-start" style={{ padding: '25px', width: '100%' }}>
                                    <iframe
                                        title="template-preview"
                                        style={{
                                            border: 'none',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                            height: '550px',
                                            overflow: 'auto',
                                            width: '100%',
                                            maxWidth: `${BILL_SIZES.find(s => s.id === selectedSize)?.width || 80}mm`
                                        }}
                                        srcDoc={generateSampleReceipt(previewTemplate, selectedSize) || '<div/>'}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TemplateManager;
